import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { Provider } from "react-redux";
import { SnackbarProvider, closeSnackbar } from "notistack";
import ConfigureStore from "./redux/store/ConfigureStore";
import reportWebVitals from "./reportWebVitals";
import { Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider } from "@mui/material/styles";
import { getTheme } from "./theme";
import { FeatureFlagProvider } from "./context/FeatureFlagContext";
import { PermissionProvider } from "./context/PermissionContext";
import { ThemeContextProvider, useThemeMode } from "./context/ThemeContext";
import { useFeatureFlags } from "./context/FeatureFlagContext";
import { LanguageProvider } from "./context/LanguageContext";
import "./i18n";

// Reads the active theme (light | dark | harmony) from ThemeContext and passes
// the matching MUI theme object down
function MuiThemeWrapper({ children }: { children: React.ReactNode }): React.ReactElement {
  const { theme } = useThemeMode();
  return <ThemeProvider theme={getTheme(theme)}>{children}</ThemeProvider>;
}

// Tenant default theme (FEATURE_FLAGS.defaultTheme, docs/26 §3): once flags load
// (post-login / cached), apply the tenant default — only while the user has no
// explicit Appearance choice (ThemeContext enforces "user choice wins").
function TenantThemeBridge(): null {
  const { defaultTheme } = useFeatureFlags();
  const { applyTenantDefault } = useThemeMode();
  React.useEffect(() => {
    applyTenantDefault(defaultTheme);
  }, [defaultTheme, applyTenantDefault]);
  return null;
}

const store = ConfigureStore();
const container = document.getElementById("root")!;
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <ThemeContextProvider>
          <MuiThemeWrapper>
            <SnackbarProvider
              preventDuplicate
              action={(snackbarId) => (
                <Button onClick={() => closeSnackbar(snackbarId)}>
                  <CloseIcon style={{ color: "#fff" }} />
                </Button>
              )}
              maxSnack={4}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
            >
              <FeatureFlagProvider>
                <TenantThemeBridge />
                <LanguageProvider>
                  <PermissionProvider>
                    <App />
                  </PermissionProvider>
                </LanguageProvider>
              </FeatureFlagProvider>
            </SnackbarProvider>
          </MuiThemeWrapper>
        </ThemeContextProvider>
      </BrowserRouter>
    </Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
